import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, DownloadSimple, UploadSimple, Export, Package, Bookmark, Flag } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { DateField } from "@/components/DateField";
import GanttChart from "@/components/GanttChart";
import EmployeeAutocomplete from "@/components/EmployeeAutocomplete";

const PHASES = ["development", "drilling", "hauling", "ground_support", "services", "ventilation", "other"];
const CANONICAL_PHASES = new Set(PHASES);
const DEP_TYPES = [
  { v: "FS", label: "FS · Fin-Inicio", hint: "Predecesora debe terminar antes de que esta empiece" },
  { v: "SS", label: "SS · Inicio-Inicio", hint: "Ambas comienzan al mismo tiempo" },
  { v: "FF", label: "FF · Fin-Fin", hint: "Ambas terminan al mismo tiempo" },
  { v: "SF", label: "SF · Inicio-Fin", hint: "La predecesora inicia y esta debe terminar" },
];
const phaseLabel = (t, phase) => (CANONICAL_PHASES.has(phase) ? t(phase) : phase);
const emptyForm = { name: "", phase: "development", start_date: "", end_date: "", progress: 0, assignee: "", dependencies: [], resources: [], milestone: false };

// Normalize a dependency entry (string legacy → {p,t,l}) for UI.
const normDep = (d) => (typeof d === "string" ? { p: d, t: "FS", l: 0 } : { p: d.p, t: d.t || "FS", l: Number(d.l) || 0 });

/**
 * Schedule tab: task CRUD, Excel import/export, Gantt chart and per-phase
 * task table with progress sliders. Tasks list is owned by the parent so it
 * can also feed the status-summary card and the detail dialog.
 *
 * Iteración 55k añade:
 *   - Checkbox "Es un hito" en el form (start=end automáticamente).
 *   - Botón "Línea base" que congela el plan actual y lo compara en el Gantt.
 *   - Columna Holgura (float days) + resaltado de tareas en camino crítico.
 */
export default function ProjectScheduleTab({
  projectId, project, tasks, resourcesCatalog,
  canWrite, canManage, onTasksChanged, setDetailTask, doExport,
}) {
  const { t } = useLang();
  const [taskOpen, setTaskOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [baseline, setBaseline] = useState({ has_baseline: false, tasks: [] });
  const [baselineOpen, setBaselineOpen] = useState(false);
  const [baselineLabel, setBaselineLabel] = useState("");
  const fileRef = useRef(null);

  const loadBaseline = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/baseline`);
      setBaseline(data || { has_baseline: false, tasks: [] });
    } catch { /* baseline es opcional, ignoramos errores */ }
  };
  useEffect(() => { loadBaseline(); }, [projectId, tasks.length]);

  const baselineByTaskId = Object.fromEntries(
    (baseline.tasks || []).map((b) => [b.task_id, b])
  );

  const saveBaseline = async () => {
    try {
      const r = await api.post(`/projects/${projectId}/baseline`, { label: baselineLabel });
      toast.success(`Línea base guardada · ${r.data.task_count} tareas congeladas`);
      setBaselineOpen(false);
      setBaselineLabel("");
      loadBaseline();
    } catch (e) { toast.error(apiError(e)); }
  };
  const clearBaseline = async () => {
    if (!window.confirm("¿Eliminar la línea base actual? Perderás la comparación planeado vs. real.")) return;
    try {
      await api.delete(`/projects/${projectId}/baseline`);
      toast.success("Línea base eliminada");
      loadBaseline();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    try {
      const payload = { ...form, progress: Number(form.progress) };
      const res = editingId
        ? await api.put(`/tasks/${editingId}`, payload)
        : await api.post(`/projects/${projectId}/tasks`, payload);
      toast.success(res?.data?._offline ? t("savedOffline") : "✓");
      setTaskOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      onTasksChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateProgress = async (task, progress) => {
    try {
      await api.put(`/tasks/${task.id}`, { ...task, progress });
      onTasksChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (taskId) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/tasks/${taskId}`);
    onTasksChanged();
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/tasks/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_gantt_ug_oomining.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/tasks/import`, fd);
      toast.success(`${data.created} ${t("importedTasks")}`);
      if (data.errors?.length) toast.error(data.errors.join(" | "));
      onTasksChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openEdit = (task) => {
    setEditingId(task.id);
    setForm({
      name: task.name, phase: task.phase, start_date: task.start_date, end_date: task.end_date,
      progress: task.progress, assignee: task.assignee,
      dependencies: (task.dependencies || []).map(normDep),
      resources: task.resources || [],
      milestone: Boolean(task.milestone),
    });
    setTaskOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {canWrite && (
          <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-task-button" onClick={() => { setEditingId(null); setForm(emptyForm); }} className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={16} weight="bold" /> {t("newTask")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editingId ? t("edit") : t("newTask")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("name")}</Label>
                  <Input data-testid="task-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("phase")}</Label>
                  <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                    <SelectTrigger data-testid="task-phase-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{PHASES.map((p) => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("assignee")}</Label>
                  <EmployeeAutocomplete testid="task-assignee-input" value={form.assignee} onChange={(v) => setForm({ ...form, assignee: v })} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("startDate")}</Label>
                  <DateField testid="task-start-date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v, end_date: form.milestone ? v : form.end_date })} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("endDate")}</Label>
                  <DateField testid="task-end-date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} disabled={form.milestone} /></div>

                <div className="col-span-2 flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
                  <Checkbox
                    id="task-milestone-checkbox"
                    data-testid="task-milestone-checkbox"
                    checked={form.milestone}
                    onCheckedChange={(c) => setForm({ ...form, milestone: !!c,
                      end_date: c ? form.start_date : form.end_date })}
                  />
                  <label htmlFor="task-milestone-checkbox" className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                    <Flag size={14} weight="fill" className="text-blue-900" />
                    Es un hito (duración cero — se dibuja como rombo)
                  </label>
                </div>

                <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("dependsOn")}</Label>
                  <DependenciesEditor
                    dependencies={form.dependencies}
                    onChange={(next) => setForm({ ...form, dependencies: next })}
                    candidates={tasks.filter((x) => x.id !== editingId)}
                  />
                </div>

                <ResourcesEditor
                  t={t}
                  resources={form.resources}
                  resourcesCatalog={resourcesCatalog}
                  onChange={(next) => setForm({ ...form, resources: next })}
                />
              </div>
              <Button data-testid="task-save-button" onClick={save} disabled={!form.name || !form.start_date || !form.end_date} className="bg-blue-900 hover:bg-blue-800 w-full">{editingId ? t("update") : t("create")}</Button>
            </DialogContent>
          </Dialog>
        )}
        {canWrite && (
          <>
            <Button variant="outline" data-testid="download-template-button" onClick={downloadTemplate} className="gap-2"><DownloadSimple size={16} weight="bold" /> {t("downloadTemplate")}</Button>
            <Button variant="outline" data-testid="import-excel-button" onClick={() => fileRef.current?.click()} className="gap-2"><UploadSimple size={16} weight="bold" /> {t("importExcel")}</Button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="import-file-input" onChange={handleImport} />
          </>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="export-schedule-button" className="gap-2"><Export size={16} weight="bold" /> {t("export")}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white w-64" align="start">
            <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Gantt</p>
            <DropdownMenuItem data-testid="export-gantt-pdf-landscape" onClick={() => doExport("gantt", "pdf", "landscape")}>Gantt — PDF ({t("orientLandscape")})</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-gantt-pdf-portrait" onClick={() => doExport("gantt", "pdf", "portrait")}>Gantt — PDF ({t("orientPortrait")})</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-gantt-xlsx" onClick={() => doExport("gantt", "xlsx")}>Gantt — Excel</DropdownMenuItem>
            <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 border-t border-slate-100 mt-1">{t("sCurve")}</p>
            <DropdownMenuItem data-testid="export-scurve-pdf-portrait" onClick={() => doExport("scurve", "pdf", "portrait")}>{t("sCurve")} — PDF ({t("orientPortrait")})</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-scurve-pdf-landscape" onClick={() => doExport("scurve", "pdf", "landscape")}>{t("sCurve")} — PDF ({t("orientLandscape")})</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-scurve-xlsx" onClick={() => doExport("scurve", "xlsx")}>{t("sCurve")} — Excel</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {canManage && (
          <div className="flex items-center gap-1.5 ml-auto" data-testid="baseline-toolbar">
            {baseline.has_baseline ? (
              <>
                <span className="text-[11px] text-slate-500 hidden md:inline">
                  Línea base: <b>{baseline.saved_at?.slice(0, 10)}</b>
                  {baseline.label ? ` · ${baseline.label}` : ""}
                </span>
                <Button variant="outline" size="sm" data-testid="baseline-clear-button" onClick={clearBaseline} className="gap-1.5 text-slate-600">
                  <Trash size={13} /> Limpiar
                </Button>
              </>
            ) : (
              <span className="text-[11px] text-slate-400 hidden md:inline">Sin línea base guardada</span>
            )}
            <Dialog open={baselineOpen} onOpenChange={setBaselineOpen}>
              <DialogTrigger asChild>
                <Button data-testid="baseline-save-button" size="sm"
                  className="bg-slate-800 hover:bg-slate-900 gap-1.5"
                  disabled={!tasks.length}>
                  <Bookmark size={14} weight="fill" />
                  {baseline.has_baseline ? "Reemplazar línea base" : "Guardar línea base"}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2">
                    <Bookmark size={18} weight="fill" className="text-slate-800" />
                    Congelar plan actual como línea base
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-600">
                  Se guardarán las fechas y % de avance de <b>{tasks.length}</b> tareas.
                  Cualquier cambio posterior aparecerá como <b>variación</b> vs. la línea base
                  en el Gantt y en la tabla.
                </p>
                <div className="space-y-1.5">
                  <Label className="overline-label">Etiqueta (opcional)</Label>
                  <Input
                    data-testid="baseline-label-input"
                    value={baselineLabel}
                    onChange={(e) => setBaselineLabel(e.target.value)}
                    placeholder="Ej. Aprobado en comité 15-feb"
                    maxLength={80}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBaselineOpen(false)}>Cancelar</Button>
                  <Button data-testid="baseline-save-confirm" onClick={saveBaseline}
                    className="bg-slate-800 hover:bg-slate-900">
                    Guardar línea base
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {tasks.length ? <GanttChart tasks={tasks} onTaskClick={setDetailTask} baselineByTaskId={baselineByTaskId} /> : <p className="text-sm text-slate-400 bg-white border border-slate-200 rounded-lg p-10 text-center">{t("noTasks")}</p>}

      {tasks.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <table className="w-full data-table">
            <thead><tr><th>{t("name")}</th><th>{t("phase")}</th><th>{t("startDate")}</th><th>{t("endDate")}</th><th className="w-20 text-right">Holgura</th><th className="w-56">{t("progress")}</th>{canWrite && <th></th>}</tr></thead>
            <tbody>
              {(() => {
                const presentCanonical = PHASES.filter((p) => tasks.some((tt) => tt.phase === p));
                const customPhases = [];
                tasks.forEach((task) => {
                  if (!CANONICAL_PHASES.has(task.phase) && !customPhases.includes(task.phase)) customPhases.push(task.phase);
                });
                return [...presentCanonical, ...customPhases].map((phase) => {
                  const group = tasks.filter((task) => task.phase === phase)
                    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
                  if (!group.length) return null;
                  return (
                    <Fragment key={`phase-${phase}`}>
                      <tr className="bg-slate-50" data-testid={`phase-group-${phase}`}>
                        <td colSpan={canWrite ? 7 : 6} className="text-xs uppercase tracking-widest font-bold text-slate-500 py-2">
                          {phaseLabel(t, phase)} <span className="text-slate-400 font-normal ml-2">({group.length})</span>
                        </td>
                      </tr>
                      {group.map((task) => (
                        <tr key={task.id} data-testid={`task-row-${task.id}`}
                          className={task.is_critical ? "bg-red-50/40" : ""}>
                          <td className="font-medium pl-6">
                            <button
                              type="button"
                              data-testid={`task-open-detail-${task.id}`}
                              onClick={() => setDetailTask(task)}
                              className="text-left hover:text-blue-900 hover:underline transition-colors inline-flex items-center gap-1.5"
                            >
                              {task.milestone && <Flag size={12} weight="fill" className="text-blue-900 shrink-0" />}
                              {task.name}
                              {task.is_critical && !task.milestone && (
                                <span className="text-[9px] font-bold uppercase tracking-widest text-red-600" data-testid={`task-critical-badge-${task.id}`}>· crítica</span>
                              )}
                            </button>
                          </td>
                          <td className="text-slate-500 text-xs uppercase tracking-wider">{phaseLabel(t, task.phase)}</td>
                          <td className="font-mono text-xs">{task.start_date}</td>
                          <td className="font-mono text-xs">{task.end_date}</td>
                          <td className="text-right text-xs font-mono">
                            <span
                              data-testid={`task-float-days-${task.id}`}
                              className={
                                task.is_critical ? "text-red-600 font-bold" :
                                task.total_float_days <= 2 ? "text-amber-600 font-semibold" :
                                "text-slate-400"
                              }
                              title={task.is_critical
                                ? "Sin holgura — retrasarla retrasa el proyecto"
                                : `${task.total_float_days} día(s) de holgura`}
                            >
                              {task.is_critical ? "0d" : `${task.total_float_days ?? 0}d`}
                            </span>
                          </td>
                          <td>
                            <div className="flex items-center gap-3">
                              {canWrite ? (
                                <Slider
                                  key={`slider-${task.id}-${task.progress}`}
                                  defaultValue={[task.progress]}
                                  max={100}
                                  step={5}
                                  className="w-32"
                                  onValueCommit={(v) => updateProgress(task, v[0])}
                                  data-testid={`task-progress-slider-${task.id}`}
                                />
                              ) : (
                                <div className="h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-900" style={{ width: `${task.progress}%` }} />
                                </div>
                              )}
                              <span className="text-xs text-slate-500 w-9" data-testid={`task-progress-value-${task.id}`}>{task.progress}%</span>
                            </div>
                          </td>
                          {canWrite && (
                            <td className="whitespace-nowrap">
                              <button data-testid={`task-edit-${task.id}`} className="text-slate-400 hover:text-blue-900 transition-colors mr-2" onClick={() => openEdit(task)}><PencilSimple size={16} /></button>
                              <button data-testid={`task-delete-${task.id}`} onClick={() => remove(task.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash size={16} /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Kept close to the schedule tab: it's only used here.
function DependenciesEditor({ dependencies, candidates, onChange }) {
  const selectedIds = new Set(dependencies.map((d) => d.p));
  const toggle = (taskId, checked) => {
    if (checked) onChange([...dependencies, { p: taskId, t: "FS", l: 0 }]);
    else onChange(dependencies.filter((d) => d.p !== taskId));
  };
  const patch = (taskId, changes) => {
    onChange(dependencies.map((d) => (d.p === taskId ? { ...d, ...changes } : d)));
  };
  const byId = Object.fromEntries(candidates.map((x) => [x.id, x]));
  return (
    <div className="space-y-2">
      {dependencies.length > 0 && (
        <div className="space-y-1.5 border border-slate-200 rounded-md p-2 bg-slate-50" data-testid="task-deps-selected">
          {dependencies.map((d) => (
            <div key={d.p} className="grid grid-cols-12 gap-2 items-center text-sm" data-testid={`task-dep-row-${d.p}`}>
              <span className="col-span-5 truncate font-medium text-slate-800" title={byId[d.p]?.name || "?"}>{byId[d.p]?.name || "(desconocida)"}</span>
              <div className="col-span-4">
                <Select value={d.t} onValueChange={(v) => patch(d.p, { t: v })}>
                  <SelectTrigger data-testid={`task-dep-type-${d.p}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {DEP_TYPES.map((tp) => (<SelectItem key={tp.v} value={tp.v} title={tp.hint}>{tp.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Input type="number" step={1} value={d.l} onChange={(e) => patch(d.p, { l: parseInt(e.target.value, 10) || 0 })}
                  className="h-8 text-xs" data-testid={`task-dep-lag-${d.p}`} title="Lag en días (negativo = adelanto)" />
              </div>
              <button type="button" onClick={() => toggle(d.p, false)}
                className="col-span-1 h-7 w-7 flex items-center justify-center text-slate-400 hover:text-red-600 justify-self-end"
                data-testid={`task-dep-remove-${d.p}`}>
                <Trash size={13} />
              </button>
            </div>
          ))}
          <p className="text-[10px] text-slate-500 mt-1"><b>FS/SS/FF/SF</b> · Lag en días (usa negativo para adelanto)</p>
        </div>
      )}
      <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1.5">
        {candidates.map((x) => (
          <label key={x.id} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid={`task-dep-checkbox-${x.id}`} checked={selectedIds.has(x.id)}
              onCheckedChange={(c) => toggle(x.id, !!c)} />
            <span className="truncate">{x.name}</span>
          </label>
        ))}
        {!candidates.length && <p className="text-xs text-slate-400">Sin otras tareas</p>}
      </div>
    </div>
  );
}

// Kept close to the schedule tab: it's only used here.
function ResourcesEditor({ t, resources, resourcesCatalog, onChange }) {
  const add = () => onChange([...resources, { resource_id: null, name: "", quantity: 1, unit: "", note: "" }]);
  const removeAt = (idx) => onChange(resources.filter((_, i) => i !== idx));
  const patch = (idx, partial) => {
    const next = [...resources];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  return (
    <div className="col-span-2 space-y-1.5" data-testid="task-resources-section">
      <div className="flex items-center justify-between">
        <Label className="overline-label flex items-center gap-1">
          <Package size={12} weight="duotone" /> {t("assignedResources")}
          <span className="text-slate-400 font-normal normal-case">({resources.length})</span>
        </Label>
        <Button type="button" variant="ghost" size="sm" data-testid="task-add-resource-button" onClick={add} className="h-7 text-xs text-blue-900">
          <Plus size={12} weight="bold" className="mr-1" /> {t("addResource")}
        </Button>
      </div>
      {resources.length === 0 ? (
        <p className="text-xs text-slate-400 italic px-1">{t("noResourcesAssigned")}</p>
      ) : (
        <div className="space-y-2 border border-slate-200 rounded-md p-2">
          {resources.map((r, idx) => (
            <div key={idx} data-testid={`task-resource-row-${idx}`} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                {resourcesCatalog.length > 0 ? (
                  <Select
                    value={r.resource_id || "__custom__"}
                    onValueChange={(v) => {
                      if (v === "__custom__") patch(idx, { resource_id: null });
                      else {
                        const cat = resourcesCatalog.find((c) => c.id === v);
                        patch(idx, { resource_id: v, name: cat?.name || "", unit: cat?.unit || r.unit });
                      }
                    }}
                  >
                    <SelectTrigger data-testid={`task-resource-catalog-${idx}`} className="h-8 text-xs">
                      <SelectValue placeholder={t("selectResource")} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__custom__">{t("customResource")}</SelectItem>
                      {resourcesCatalog.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400 text-xs">({t(c.type)})</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                {(!r.resource_id || resourcesCatalog.length === 0) && (
                  <Input data-testid={`task-resource-name-${idx}`} placeholder={t("resourceName")} className="h-8 text-xs mt-1"
                    value={r.name} onChange={(e) => patch(idx, { name: e.target.value })} />
                )}
              </div>
              <div className="col-span-2">
                <Input data-testid={`task-resource-qty-${idx}`} type="number" min={0} step={0.1} placeholder={t("qty")} className="h-8 text-xs"
                  value={r.quantity} onChange={(e) => patch(idx, { quantity: Number(e.target.value) })} />
              </div>
              <div className="col-span-2">
                <Input data-testid={`task-resource-unit-${idx}`} placeholder={t("unit")} className="h-8 text-xs"
                  value={r.unit} onChange={(e) => patch(idx, { unit: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Input data-testid={`task-resource-note-${idx}`} placeholder={t("resourceNote")} className="h-8 text-xs"
                  value={r.note} onChange={(e) => patch(idx, { note: e.target.value })} />
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" data-testid={`task-resource-remove-${idx}`} onClick={() => removeAt(idx)} className="h-7 w-7 flex items-center justify-center text-slate-400 hover:text-red-600 transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
